/**
 * useOrgRole.ts — WEB-343
 *
 * Returns the current user's role in the active organisation.
 *
 * Source of truth: state.organizations.orgs[activeOrgId].role
 * (populated from GET /organizations — the list response includes the caller's
 * OrgRole per org as defined in the Organization FE type).
 *
 * Mirrors the useProjectRole pattern — pure selector, no side-effects.
 *
 * Follow-up: if the BE list response does not yet include role, the hook degrades
 * gracefully (role → null, isOrgOwner/isOrgAdmin → false).
 *
 * WEB-562 A0: role can also be 'member' — a UI-only label for an org reached
 * ONLY via project-membership (no organization_members row). isOrgOwner/
 * isOrgAdmin stay false for it: org-settings/team/admin endpoints are
 * Owner/Admin-only on the backend and 403 for this role.
 *
 * WEB-1417: optional `orgId` — role is resolved in THAT org (the project's
 * own org for project-scoped rights), not the active one. The server judges
 * by the project's org, so the FE must check against the same org — with the
 * active-org reading a user in two orgs saw a delete button that 403'd (or
 * a real org-admin saw it disabled). No argument = old behavior (active org).
 */
import { useAppSelector } from './hooks';
import type { OrgRoleLabel } from '@/types';

export interface OrgRoleInfo {
  /** Caller's role in the resolved org, or null when no org is selected. */
  role: OrgRoleLabel | null;
  /** true only for OWNER. */
  isOrgOwner: boolean;
  /** true for OWNER and ADMIN. */
  isOrgAdmin: boolean;
  /**
   * WEB-1343 остаток C2: право распоряжаться деньгами организации —
   * ОТДЕЛЬНОЕ от «управляет организацией». Владелец организации им обладает
   * всегда, администратор — пока владелец не снял ему доступ к платёжным
   * данным. Признак приходит с сервера готовым
   * (OrganizationResponseDto.canManageBilling) и считается там тем же
   * предикатом, каким сервер судит денежные пути организации, — кабинет
   * зеркалит решение, а не выводит его заново из роли.
   *
   * Строго `=== true`: объект организации без поля (старый ответ, кеш) —
   * «нет права», fail-closed.
   */
  canManageOrgBilling: boolean;
  /** ID of the resolved org (given orgId, else the active org), or null. */
  orgId: string | null;
  /** Name of the resolved org, or null. */
  orgName: string | null;
}

export function useOrgRole(orgId?: string): OrgRoleInfo {
  const activeOrgId = useAppSelector(
    (state) => state.organizations.activeOrgId,
  );
  const orgs = useAppSelector((state) => state.organizations.orgs);

  // WEB-1417: explicit orgId wins over the active org.
  const targetOrgId = orgId ?? activeOrgId;
  const targetOrg = targetOrgId
    ? (orgs.find((o) => o.id === targetOrgId) ?? null)
    : null;

  const role: OrgRoleLabel | null = targetOrg?.role ?? null;

  return {
    role,
    isOrgOwner: role === 'owner',
    isOrgAdmin: role === 'owner' || role === 'admin',
    canManageOrgBilling: targetOrg?.canManageBilling === true,
    orgId: targetOrgId,
    orgName: targetOrg?.name ?? null,
  };
}
